/** Planning · Variância orçado × realizado por conta e centro de custo (tela 8.3). */
import { z } from 'zod'
import { dataNominal, uuid } from './comum.js'

/**
 * A menor diferença que a tela consegue mostrar, transportada do protótipo.
 *
 * A 8.3 exibe em milhares com uma casa (`179,9k`), então R$ 100 é o passo visível e
 * R$ 50 é a metade dele — abaixo disso a tela escreve "0". Pintar de verde uma
 * diferença que aparece como zero é o tipo de contradição que faz quem lê desconfiar
 * do resto da página, e por isso o limiar é do CONTRATO, não de cada superfície: a
 * API decide o sentido e a tela apenas o desenha.
 */
export const MINIMO_EXIBIVEL_DA_VARIANCIA = '50.00'

export const sentidoDaVariancia = z.enum(['favoravel', 'desfavoravel', 'neutra'])
export type SentidoDaVarianciaApi = z.infer<typeof sentidoDaVariancia>

export const filtrosDaVariancia = z.object({
  versaoId: uuid.optional(),
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  centroCustoId: uuid.optional(),
})
export type FiltrosDaVariancia = z.infer<typeof filtrosDaVariancia>

/** Um recorte comparado (Período ou YTD). Dinheiro em decimal de máquina; null quando restrito. */
export const recorteDaVariancia = z.object({
  orcado: z.string().nullable(),
  realizado: z.string().nullable(),
  variacao: z.string().nullable(),
  percentual: z.string().nullable(),
  sentido: sentidoDaVariancia,
  /**
   * D-181: só na linha Calculada sem pendência no recorte. `flexivel` é a regra congelada × a receita realizada;
   * `volume` = flexível − orçado; `aliquota` = realizado − flexível, de onde vem o sentido. Ausente na API anterior.
   */
  decomposicao: z.object({
    flexivel: z.string().nullable(),
    volume: z.string().nullable(),
    aliquota: z.string().nullable(),
  }).nullable().optional(),
})
export type RecorteDaVariancia = z.infer<typeof recorteDaVariancia>

export const anoCheioDaVariancia = z.object({
  orcado: z.string().nullable(),
  /** YTD realizado ÷ FY orçado, em percentual. `null` quando não há orçado do ano. */
  execucao: z.string().nullable(),
})
export type AnoCheioDaVariancia = z.infer<typeof anoCheioDaVariancia>

/**
 * Uma linha da tabela = um par (conta analítica × centro de custo).
 *
 * O par é o grão porque o centro de custo é dimensão do LANÇAMENTO e da LINHA DE
 * ORÇAMENTO, nunca atributo da conta (I-26). A mesma conta aparece em N linhas quando
 * foi orçada ou realizada em N centros — e `centroCustoId` nulo é o caso legítimo de
 * quem não tem centro (receita, dedução, amortização) ou de quem foi orçado sem ele.
 */
export const linhaDaVariancia = z.object({
  contaContabilId: uuid,
  contaCodigo: z.string(),
  contaNome: z.string(),
  centroCustoId: uuid.nullable(),
  centroCustoNome: z.string().nullable(),
  periodo: recorteDaVariancia,
  ytd: recorteDaVariancia,
  fy: anoCheioDaVariancia,
})
export type LinhaDaVariancia = z.infer<typeof linhaDaVariancia>

export const grupoDaVariancia = z.object({
  contaContabilId: uuid,
  codigo: z.string(),
  nome: z.string(),
  natureza: z.enum(['receita', 'gasto']),
  periodo: recorteDaVariancia,
  ytd: recorteDaVariancia,
  fy: anoCheioDaVariancia,
  linhas: z.array(linhaDaVariancia),
})
export type GrupoDaVariancia = z.infer<typeof grupoDaVariancia>

/**
 * Os quatro KPIs. Tudo anulável porque "sem meses fechados" é estado VÁLIDO: o mês
 * corrente ainda não foi apurado, e escrever zero ali afirmaria um faturamento que
 * ninguém mediu.
 */
export const kpisDaVariancia = z.object({
  faturamentoPeriodo: z.string().nullable(),
  faturamentoDeltaPct: z.string().nullable(),
  gastosPeriodo: z.string().nullable(),
  gastosDeltaPct: z.string().nullable(),
  varianciaLiquida: z.string().nullable(),
  /** Sobre o RESULTADO, onde favorável não depende da natureza de nenhuma conta. */
  varianciaLiquidaFavoravel: z.boolean().nullable(),
  execucaoReceitaFy: z.string().nullable(),
  receitaYtd: z.string().nullable(),
  receitaOrcadaFy: z.string().nullable(),
})
export type KpisDaVariancia = z.infer<typeof kpisDaVariancia>

export const versaoNaVariancia = z.object({
  id: uuid,
  rotulo: z.string(),
  estado: z.enum(['rascunho', 'submetida', 'aprovada', 'bloqueada', 'fechada']),
  /** DS-623: meses calculados pendentes; a versão incompleta é marcada no seletor. Ausente na API anterior. */
  pendencias: z.number().int().optional(),
})
export type VersaoNaVariancia = z.infer<typeof versaoNaVariancia>

/** O intervalo de meses fechados coberto pelo período; `null` = sem interseção. */
export const janelaFechada = z.object({ de: dataNominal, ate: dataNominal })
export type JanelaFechada = z.infer<typeof janelaFechada>

export const respostaDaVariancia = z.object({
  data: z.object({
    grupos: z.array(grupoDaVariancia),
    kpis: kpisDaVariancia,
  }),
  meta: z.object({
    periodoDe: dataNominal,
    periodoAte: dataNominal,
    exercicio: z.number().int(),
    /** Meses fechados do exercício, 1..12 — a régua do que pode ser comparado. */
    mesesFechados: z.array(z.number().int().min(1).max(12)),
    periodoApurado: janelaFechada.nullable(),
    ytd: janelaFechada.nullable(),
    versao: versaoNaVariancia.nullable(),
    versoes: z.array(versaoNaVariancia),
    entidade: z.object({ nome: z.string(), temOrcamento: z.boolean() }),
    /**
     * A tela precisa SABER disso, não só receber "—".
     *
     * Sem permissão de ver valores o drill não abre (spec 04-TELAS/8.3 §Permissões),
     * e resolver isso só no servidor deixaria a linha convidando para um clique que
     * termina em 404. Com o campo, a linha deixa de ser botão.
     */
    veValoresFinanceiros: z.boolean(),
    centroCusto: z.object({ id: uuid, nome: z.string() }).nullable(),
    /**
     * Orçado que ficou FORA da tabela por não ter conta contábil.
     *
     * A grade da 8.2 aceita linha sem classificação, e a 8.3 é uma leitura por conta:
     * sem conta, a linha não tem lugar na árvore. Some-lo em silêncio esconderia
     * dinheiro planejado — a tela nomeia o que ficou de fora (DS-99/DS-211).
     */
    orcadoSemClassificacao: z.string().nullable(),
    moeda: z.literal('BRL'),
    podeExportar: z.boolean(),
  }),
})
export type RespostaDaVariancia = z.infer<typeof respostaDaVariancia>

// ------------------------------------------------------------------ drill

export const filtrosDosLancamentos = z.object({
  contaContabilId: uuid,
  centroCustoId: uuid.optional(),
  /** `true` quando a linha clicada é a do centro de custo ausente ("CC —"). */
  semCentroCusto: z.boolean().optional(),
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  versaoId: uuid.optional(),
})
export type FiltrosDosLancamentos = z.infer<typeof filtrosDosLancamentos>

/** DS-614: o fato bancário autorizado (avulso categorizado de conciliação) também é lançamento da DRE. */
// As origens que a v_dre_lancamento entrega: a v1.5.0 acrescentou a provisão e o ajuste da apuração (DS-616) e a
// comissão pelo gatilho e o ajuste dela (DS-619). Origem fora daqui quebrava o detalhe da Variância.
export const origemDoLancamento = z.enum([
  'nf', 'deducao', 'cap', 'amortizacao', 'encargo', 'fato_bancario',
  'provisao_tributaria', 'ajuste_tributario', 'comissao', 'ajuste_comissao',
])
export type OrigemDoLancamento = z.infer<typeof origemDoLancamento>

export const lancamentoDaVariancia = z.object({
  origem: origemDoLancamento,
  origemId: uuid,
  documento: z.string(),
  descricao: z.string(),
  data: dataNominal,
  valor: z.string(),
})
export type LancamentoDaVariancia = z.infer<typeof lancamentoDaVariancia>

export const respostaDosLancamentos = z.object({
  data: z.object({
    lancamentos: z.array(lancamentoDaVariancia),
    /** A soma dos listados. É o realizado da linha — vem da mesma fonte, não de outra. */
    total: z.string(),
  }),
  meta: z.object({
    conta: z.object({ id: uuid, codigo: z.string(), nome: z.string(), grupo: z.string() }),
    centroCusto: z.object({ id: uuid, nome: z.string() }).nullable(),
    periodoDe: dataNominal,
    periodoAte: dataNominal,
    periodoApurado: janelaFechada.nullable(),
    orcado: z.string(),
    moeda: z.literal('BRL'),
  }),
})
export type RespostaDosLancamentos = z.infer<typeof respostaDosLancamentos>
